import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Badge, 
  alpha, 
  Skeleton, 
  IconButton, 
  TextField, 
  InputAdornment 
} from '@mui/material';
import { Droppable } from 'react-beautiful-dnd';
import { 
  Add as AddIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  Clear as ClearIcon
} from '@mui/icons-material';
import LeadCard from './LeadCard';
import { getNewLeadData, getQualifiedLeadData, getOtherLeadData } from '../../../../Action/api';

const Column = ({ column, leads, onViewDetails, updating, onRefresh, month, onColumnLeadsUpdate }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [localLeads, setLocalLeads] = useState(leads);

  // Update local leads when parent leads change
  useEffect(() => {
    setLocalLeads(leads);
  }, [leads]);

  // Filter leads based on search term
  const filteredLeads = localLeads.filter(lead => {
    if (!searchTerm) return true;
    
    const searchLower = searchTerm.toLowerCase();
    const name = (lead.fullName || `${lead.firstName || ''} ${lead.lastName || ''}`.trim()).toLowerCase();
    const email = (lead.email || '').toLowerCase();
    const phone = (lead.phoneNumber || lead.phone || '').toLowerCase();
    const location = (lead.location || '').toLowerCase();
    
    return name.includes(searchLower) ||
           email.includes(searchLower) ||
           phone.includes(searchLower) ||
           location.includes(searchLower);
  });

  const handleRefresh = async () => {
    if (column.id === 'New') {
      setIsRefreshing(true);
      try {
        const response = await getNewLeadData(month);
        if (response.status && response.data) {
          setLocalLeads(response.data);
          if (onColumnLeadsUpdate) {
            onColumnLeadsUpdate(column.id, response.data);
          }
        }
      } catch (error) {
        console.error('Error refreshing leads:', error);
      } finally {
        setIsRefreshing(false);
      }
    }
    else if (column.id === 'Qualified') {
      setIsRefreshing(true);
      try {
        const response = await getQualifiedLeadData(month);
        if (response.status && response.data) {
          setLocalLeads(response.data);
          if (onColumnLeadsUpdate) {
            onColumnLeadsUpdate(column.id, response.data);
          }
        }
      } catch (error) {
        console.error('Error refreshing leads:', error);
      } finally {
        setIsRefreshing(false);
      }
    }
    // Common refresh for Disqualified, Closed Won, Closed - Won, Closed Loss, Closed - Loss
    else if (
      column.id === 'Disqualified' ||
      column.id === 'Closed Won' || column.id === 'Closed - Won' ||
      column.id === 'Closed Loss' || column.id === 'Closed - Loss'
    ) {
      setIsRefreshing(true);
      try {
        const response = await getOtherLeadData(month);
        if (response.status && response.data) {
          // Filter leads for this column based on leadType
          const filtered = response.data.filter(
            lead => {
              // Map column.id to leadType value in API response
              if (column.id === 'Closed Won') return lead.leadType === 'Closed - Won';
              if (column.id === 'Closed Loss') return lead.leadType === 'Closed - Loss';
              return lead.leadType === column.id;
            }
          );
          setLocalLeads(filtered);
          if (onColumnLeadsUpdate) {
            onColumnLeadsUpdate(column.id, filtered);
          }
        }
      } catch (error) {
        console.error('Error refreshing leads:', error);
      } finally {
        setIsRefreshing(false);
      }
    }
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleClearSearch = () => {
    setSearchTerm('');
  };

  return (
    <Paper sx={{ 
      minHeight: '400px',
      height: 'auto',
      maxHeight: 'none',
      display: 'flex',
      flexDirection: 'column',
      borderRadius: 4,
      overflow: 'visible',
      background: 'linear-gradient(145deg, #ffffff 0%, #f8fafc 100%)',
      boxShadow: `
        0 20px 25px -5px ${alpha(column.color, 0.1)},
        0 10px 10px -5px ${alpha(column.color, 0.04)},
        0 0 0 1px ${alpha(column.color, 0.05)}
      `,
      border: `1px solid ${alpha(column.color, 0.1)}`,
      position: 'relative',
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: `
          0 32px 64px -12px ${alpha(column.color, 0.2)},
          0 25px 50px -12px ${alpha(column.color, 0.15)},
          0 0 0 1px ${alpha(column.color, 0.1)}
        `,
      }
    }}>
      
      {/* Premium Column Header */}
      <Box sx={{ 
        position: 'relative',
        background: column.gradient,
        p: 2.5,
        overflow: 'hidden',
        flexShrink: 0,
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `linear-gradient(135deg, ${alpha('#ffffff', 0.1)} 0%, transparent 50%, ${alpha('#000000', 0.1)} 100%)`,
          pointerEvents: 'none'
        },
        '&::after': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '2px',
          background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.5), transparent)',
        }
      }}>
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          position: 'relative',
          zIndex: 2
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{ 
              width: 32,
              height: 32,
              borderRadius: 2,
              background: 'rgba(255,255,255,0.2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255,255,255,0.3)'
            }}>
              {column.icon}
            </Box>
            <Typography 
              variant="h6" 
              sx={{ 
                fontWeight: 700,
                color: 'white',
                fontSize: '1.1rem',
                letterSpacing: '0.5px',
                textShadow: '0 2px 4px rgba(0,0,0,0.2)'
              }}
            >
              {column.title}
            </Typography>
          </Box>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {(column.id === 'New' ||
              column.id === 'Qualified' ||
              column.id === 'Disqualified' ||
              column.id === 'Closed Won' || column.id === 'Closed - Won' ||
              column.id === 'Closed Loss' || column.id === 'Closed - Loss') && (
              <IconButton
                size="small"
                onClick={handleRefresh}
                disabled={isRefreshing}
                sx={{
                  color: 'white',
                  background: 'rgba(255,255,255,0.1)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255,255,255,0.2)',
                  width: 32,
                  height: 32,
                  '&:hover': {
                    background: 'rgba(255,255,255,0.2)',
                    transform: 'scale(1.1)'
                  },
                  '&:disabled': {
                    color: 'rgba(255,255,255,0.5)'
                  }
                }}
              >
                <RefreshIcon 
                  sx={{ 
                    fontSize: 16,
                    animation: isRefreshing ? 'spin 1s linear infinite' : 'none',
                    '@keyframes spin': {
                      '0%': { transform: 'rotate(0deg)' },
                      '100%': { transform: 'rotate(360deg)' }
                    }
                  }} 
                />
              </IconButton>
            )}
            
            <Badge 
              badgeContent={filteredLeads.length} 
              sx={{
                '& .MuiBadge-badge': {
                  background: 'rgba(255,255,255,0.9)',
                  color: column.color,
                  fontWeight: 800,
                  fontSize: '0.75rem',
                  minWidth: 24,
                  height: 24,
                  borderRadius: 2,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                  border: '1px solid rgba(255,255,255,0.3)'
                }
              }}
            />
          </Box>
        </Box>
      </Box>

      {/* Column Search Box */}
      <Box sx={{ p: 2, pb: 1 }}>
        <TextField
          size="small"
          placeholder={`Search in ${column.title}...`}
          value={searchTerm}
          onChange={handleSearchChange}
          fullWidth
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: alpha(column.color, 0.6), fontSize: 18 }} />
              </InputAdornment>
            ),
            endAdornment: searchTerm && (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  onClick={handleClearSearch}
                  sx={{ 
                    p: 0.5,
                    color: alpha(column.color, 0.6),
                    '&:hover': { color: column.color }
                  }}
                >
                  <ClearIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </InputAdornment>
            )
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
              fontSize: '0.85rem',
              background: alpha('#ffffff', 0.8),
              backdropFilter: 'blur(10px)',
              border: `1px solid ${alpha(column.color, 0.2)}`,
              '&:hover': {
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: alpha(column.color, 0.4)
                }
              },
              '&.Mui-focused': {
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: column.color,
                  borderWidth: 2
                }
              }
            },
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: alpha(column.color, 0.2)
            },
            '& .MuiInputBase-input': {
              py: 1,
              '&::placeholder': {
                color: alpha(column.color, 0.5),
                fontSize: '0.8rem'
              }
            }
          }}
        />
        
        {/* Search Results Indicator */}
        {searchTerm && (
          <Typography variant="caption" sx={{ 
            display: 'block',
            mt: 0.5,
            color: alpha(column.color, 0.7),
            fontSize: '0.7rem',
            fontWeight: 500
          }}>
            {filteredLeads.length} of {leads.length} leads
          </Typography>
        )}
      </Box>

      {/* Column Content */}
      <Droppable droppableId={column.id}>
        {(provided, snapshot) => (
          <Box
            ref={provided.innerRef}
            {...provided.droppableProps}
            sx={{
              flex: 'none',
              minHeight: 320, // Ensures drop area is always visible
              p: 2,
              pt: 1,
              background: snapshot.isDraggingOver 
                ? `linear-gradient(135deg, ${alpha(column.color, 0.12)} 0%, ${alpha(column.color, 0.04)} 100%)`
                : 'transparent',
              border: snapshot.isDraggingOver 
                ? `2px dashed ${column.color}` 
                : `2px dashed transparent`,
              boxShadow: snapshot.isDraggingOver 
                ? `0 0 0 4px ${alpha(column.color, 0.08)}`
                : 'none',
              borderRadius: 3,
              transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
              position: 'relative',
              overflowY: 'auto'
            }}
          >
            {filteredLeads.length === 0 ? (
              <Box sx={{ 
                textAlign: 'center', 
                py: 4,
                px: 2,
                position: 'relative',
                zIndex: 1,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                minHeight: '160px'
              }}>
                {/* Compact Empty State */}
                <Box sx={{ 
                  p: 3,
                  borderRadius: 3,
                  background: snapshot.isDraggingOver 
                    ? `linear-gradient(145deg, ${alpha(column.color, 0.1)} 0%, ${alpha(column.color, 0.05)} 100%)`
                    : `linear-gradient(145deg, ${alpha('#f8fafc', 0.6)} 0%, ${alpha('#ffffff', 0.8)} 100%)`,
                  border: `2px dashed ${snapshot.isDraggingOver ? column.color : alpha(column.color, 0.3)}`,
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  transform: snapshot.isDraggingOver ? 'scale(1.02)' : 'scale(1)',
                  boxShadow: snapshot.isDraggingOver 
                    ? `0 8px 20px ${alpha(column.color, 0.15)}`
                    : `0 4px 12px ${alpha('#000000', 0.03)}`,
                  width: '100%',
                  maxWidth: '200px'
                }}>
                  <Box sx={{ 
                    width: 48,
                    height: 48,
                    borderRadius: '50%', 
                    background: snapshot.isDraggingOver 
                      ? column.gradient
                      : `linear-gradient(145deg, ${alpha(column.color, 0.2)} 0%, ${alpha(column.color, 0.1)} 100%)`,
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    margin: '0 auto 16px',
                    transition: 'all 0.3s ease',
                    transform: snapshot.isDraggingOver ? 'scale(1.1)' : 'scale(1)',
                  }}>
                    <AddIcon sx={{ 
                      fontSize: 24,
                      color: snapshot.isDraggingOver ? 'white' : column.color,
                      transition: 'all 0.3s ease'
                    }} />
                  </Box>
                  
                  <Typography variant="subtitle1" sx={{ 
                    fontWeight: 600, 
                    mb: 1, 
                    color: snapshot.isDraggingOver ? column.color : '#64748b',
                    fontSize: '0.9rem',
                    transition: 'color 0.3s ease'
                  }}>
                    {searchTerm 
                      ? 'No matching leads' 
                      : snapshot.isDraggingOver 
                        ? 'Drop here!' 
                        : 'No leads yet'
                    }
                  </Typography>
                  
                  <Typography variant="caption" sx={{ 
                    color: '#94a3b8',
                    lineHeight: 1.4,
                    fontSize: '0.75rem',
                    display: 'block'
                  }}>
                    {searchTerm 
                      ? 'Try different keywords'
                      : snapshot.isDraggingOver 
                        ? 'Release to add lead'
                        : 'Drag leads here'
                    }
                  </Typography>
                </Box>
              </Box>
            ) : (
              <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column',
                gap: 2,
                pb: 2,
                position: 'relative',
                zIndex: 1
              }}>
                {updating || isRefreshing ? (
                  Array.from({ length: Math.min(3, filteredLeads.length) }).map((_, index) => (
                    <Skeleton
                      key={index}
                      variant="rectangular"
                      height={120}
                      sx={{ 
                        borderRadius: 3,
                        bgcolor: alpha(column.color, 0.05)
                      }}
                    />
                  ))
                ) : (
                  filteredLeads.map((lead, index) => (
                    <LeadCard
                      key={lead.leadId}
                      lead={lead}
                      index={index}
                      onViewDetails={onViewDetails}
                      columnColor={column.color}
                      columnGradient={column.gradient}
                    />
                  ))
                )}
              </Box>
            )}
            {provided.placeholder}
          </Box>
        )}
      </Droppable>
    </Paper>
  );
};

export default Column;